import React, {useEffect, useState} from 'react';
import {Link, useHistory, useLocation} from 'react-router-dom';
import axios from 'axios';
import Wrapper from '../Wrapper';
import {Survey} from '../../classes/survey';

type SubmissionForm = {xmlFormId: string; name: string; submissions: number};
type Submission = {
    id: string;
    submitted_at: string | null;
    submitted_by: string | null;
    review_state: string | null;
    institution: string | null;
    reporting_year: string | number | null;
    answers: Record<string, unknown>;
};
type SubmissionPage = {
    data: Submission[];
    meta: {total: number; page: number; last_page: number};
};

function answerFields(value: unknown, path: string[] = []): Array<{label: string; value: string}> {
    if (value !== null && typeof value === 'object') {
        return Object.entries(value).flatMap(([key, child]) => answerFields(child, [...path, key]));
    }
    return [{label: path.join(' / ').replace(/_/g, ' '), value: value == null || value === '' ? '—' : String(value)}];
}

function Submissions() {
    const history = useHistory();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const [surveys, setSurveys] = useState<Survey[]>([]);
    const [surveysLoading, setSurveysLoading] = useState(true);
    const [surveysError, setSurveysError] = useState('');
    const [refresh, setRefresh] = useState(0);
    const [formsState, setFormsState] = useState({
        surveyId: '', forms: [] as SubmissionForm[], loading: false, error: '',
    });
    const [rowsState, setRowsState] = useState({
        key: '', data: [] as Submission[], total: 0, lastPage: 1, loading: false, error: '',
    });
    const [expanded, setExpanded] = useState<string | null>(null);
    const surveyId = params.get('survey') || (surveys[0] ? String(surveys[0].id) : '');
    const survey = surveys.find(item => String(item.id) === surveyId);
    const forms = formsState.surveyId === surveyId ? formsState.forms : [];
    const requestedForm = params.get('form');
    const form = forms.find(item => item.xmlFormId === (requestedForm || forms[0]?.xmlFormId));
    const formId = form?.xmlFormId || '';
    const requestedPage = Number(params.get('page') || 1);
    const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
    const rowsKey = `${surveyId}:${formId}:${page}:${refresh}`;
    const rows = rowsState.key === rowsKey ? rowsState.data : [];
    const formsLoading = Boolean(survey) && (formsState.surveyId !== surveyId || formsState.loading);
    const rowsLoading = Boolean(formId) && (rowsState.key !== rowsKey || rowsState.loading);
    const error = surveysError || (formsState.surveyId === surveyId && formsState.error)
        || (rowsState.key === rowsKey && rowsState.error);
    const loading = surveysLoading || formsLoading || rowsLoading;
    const total = rowsState.key === rowsKey ? rowsState.total : 0;
    const lastPage = rowsState.key === rowsKey ? rowsState.lastPage : 1;
    const expandedSubmission = rows.find(item => item.id === expanded);

    useEffect(() => {
        const source = axios.CancelToken.source();
        setSurveysLoading(true);
        setSurveysError('');
        (async () => {
            try {
                const first = await axios.get('surveys/', {
                    params: {page: 1, page_size: 1000}, cancelToken: source.token,
                });
                const items: Survey[] = [...first.data.data];
                // The local survey API reports the record count in last_page.
                const pages = Math.ceil(first.data.meta.last_page / first.data.meta.page_size);
                for (let current = 2; current <= pages; current++) {
                    const response = await axios.get('surveys/', {
                        params: {page: current, page_size: 1000}, cancelToken: source.token,
                    });
                    items.push(...response.data.data);
                }
                setSurveys(items);
                setSurveysLoading(false);
            } catch (err) {
                if (axios.isCancel(err)) return;
                setSurveysError('Unable to load surveys. Please try again.');
                setSurveysLoading(false);
            }
        })();
        return () => source.cancel();
    }, [refresh]);

    const accessibleSurveyId = survey ? surveyId : '';
    useEffect(() => {
        if (!accessibleSurveyId) return;
        const source = axios.CancelToken.source();
        setFormsState({surveyId: accessibleSurveyId, forms: [], loading: true, error: ''});
        axios.get(`surveys/${accessibleSurveyId}/submission-forms/`, {cancelToken: source.token})
            .then(response => setFormsState({
                surveyId: accessibleSurveyId, forms: response.data.data, loading: false, error: '',
            }))
            .catch(err => {
                if (axios.isCancel(err)) return;
                setFormsState({surveyId: accessibleSurveyId, forms: [], loading: false,
                    error: 'Unable to load forms with submissions. Please try again.'});
            });
        return () => source.cancel();
    }, [accessibleSurveyId, refresh]);

    useEffect(() => {
        if (!formId || !accessibleSurveyId) return;
        const source = axios.CancelToken.source();
        setRowsState({key: rowsKey, data: [], total: 0, lastPage: 1, loading: true, error: ''});
        axios.get<SubmissionPage>(
            `surveys/${accessibleSurveyId}/submission-forms/${encodeURIComponent(formId)}/`,
            {params: {page}, cancelToken: source.token},
        ).then(response => setRowsState({key: rowsKey, data: response.data.data,
            total: response.data.meta.total, lastPage: response.data.meta.last_page, loading: false, error: ''}))
            .catch(err => {
                if (axios.isCancel(err)) return;
                setRowsState({key: rowsKey, data: [], total: 0, lastPage: 1, loading: false,
                    error: 'Unable to load submissions. Please try again.'});
            });
        return () => source.cancel();
    }, [accessibleSurveyId, formId, page, rowsKey]);

    const select = (key: 'survey' | 'form' | 'page', value: string) => {
        const next = new URLSearchParams(location.search);
        next.set('survey', surveyId);
        if (formId) next.set('form', formId);
        next.set(key, value);
        if (key === 'survey') next.delete('form');
        if (key !== 'page') next.delete('page');
        setExpanded(null);
        history.push({pathname: '/submissions', search: next.toString()});
    };

    return (
        <Wrapper>
            <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                <h2 className="mb-0">Submissions</h2>
                <button type="button" className="btn btn-outline-secondary btn-sm" disabled={loading}
                        onClick={() => {setExpanded(null); setRefresh(value => value + 1);}}>Refresh</button>
            </div>
            <Link to="/surveys">Back to surveys</Link>
            <div className="row g-3 my-3">
                <div className="col-12 col-lg-6">
                    <label htmlFor="submission-survey" className="form-label">Survey</label>
                    <select id="submission-survey" className="form-select" value={survey ? surveyId : ''}
                            disabled={surveysLoading || surveys.length === 0}
                            onChange={event => select('survey', event.target.value)}>
                        <option value="" disabled>Select a survey</option>
                        {surveys.map(item => <option key={item.id} value={item.id}>{item.name} — {item.country}</option>)}
                    </select>
                </div>
                <div className="col-12 col-lg-6">
                    <label htmlFor="submission-form" className="form-label">Forms with submissions</label>
                    <select id="submission-form" className="form-select" value={formId}
                            disabled={formsLoading || forms.length === 0}
                            onChange={event => select('form', event.target.value)}>
                        <option value="" disabled>Select a form</option>
                        {forms.map(item => <option key={item.xmlFormId} value={item.xmlFormId}>
                            {item.name} ({item.submissions})
                        </option>)}
                    </select>
                </div>
            </div>
            {error && <div className="alert alert-danger" role="alert">{error} Use Refresh to retry.</div>}
            <div role="status" aria-live="polite" className="mb-3">
                {loading ? 'Loading submissions…' : !error && (
                    !survey ? (surveys.length ? 'This survey is unavailable.' : 'No surveys are available.')
                        : !form ? (forms.length ? 'This form has no submissions for this survey.'
                            : `No forms with submissions for ${survey.name} (${survey.country}).`)
                            : `${total} submission${total === 1 ? '' : 's'} for ${survey.country}`
                )}
            </div>
            {form && !error && (
                <>
                    <div className="table-responsive" aria-busy={rowsLoading}>
                        <table className="table table-striped table-sm">
                            <thead><tr>
                                <th>Submitted</th><th>Country</th><th>Institution</th><th>Reporting year</th>
                                <th>Submitted by</th><th>Review status</th><th>Responses</th>
                            </tr></thead>
                            <tbody>
                                {rows.map(item => <tr key={item.id}>
                                    <td>{item.submitted_at ? new Date(item.submitted_at).toLocaleString() : '—'}</td>
                                    <td>{survey?.country}</td><td>{item.institution || '—'}</td>
                                    <td>{item.reporting_year ?? '—'}</td><td>{item.submitted_by || '—'}</td>
                                    <td>{item.review_state || 'Not reviewed'}</td>
                                    <td><button type="button" className="btn btn-outline-primary btn-sm"
                                                aria-expanded={expanded === item.id} aria-controls="submission-answers"
                                                onClick={() => setExpanded(expanded === item.id ? null : item.id)}>
                                        {expanded === item.id ? 'Hide responses' : 'View responses'}
                                    </button></td>
                                </tr>)}
                                {!rowsLoading && rows.length === 0 && <tr><td colSpan={7}>No submissions on this page.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                    <nav className="d-flex align-items-center gap-3 mb-3" aria-label="Submissions pagination">
                        <button type="button" className="btn btn-outline-primary btn-sm"
                                disabled={rowsLoading || page <= 1} onClick={() => select('page', String(page - 1))}>Previous</button>
                        <span>Page {page} of {lastPage}</span>
                        <button type="button" className="btn btn-outline-primary btn-sm"
                                disabled={rowsLoading || page >= lastPage} onClick={() => select('page', String(page + 1))}>Next</button>
                    </nav>
                </>
            )}
            {expandedSubmission && <section id="submission-answers" className="card mb-4" aria-label="Submitted responses">
                <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <h3 className="h5 mb-0">Submitted responses</h3>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setExpanded(null)}>Close</button>
                    </div>
                    <dl className="row mb-0">
                        {answerFields(expandedSubmission.answers).map((answer, index) => <React.Fragment key={index}>
                            <dt className="col-md-5 text-break">{answer.label}</dt>
                            <dd className="col-md-7 text-break" style={{whiteSpace: 'pre-wrap'}}>{answer.value}</dd>
                        </React.Fragment>)}
                    </dl>
                </div>
            </section>}
        </Wrapper>
    );
}

export default Submissions;
